import { Brain, CheckCircle, AlertTriangle, TrendingUp, MessageSquare, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function AIMonitoringPage() {

  // Mock AI monitoring integrations data
  const aiIntegrations = [
    {
      id: 1,
      name: 'Slack',
      type: 'Communication',
      status: 'active',
      risksDetected: 12,
      channels: 45,
      keywords: ['incident', 'outage', 'error', 'down', 'critical'],
      lastScan: '2 minutes ago',
      icon: MessageSquare,
      color: 'text-purple-400'
    },
    {
      id: 2,
      name: 'Microsoft Teams',
      type: 'Communication',
      status: 'active',
      risksDetected: 8,
      channels: 32,
      keywords: ['issue', 'problem', 'failure', 'urgent'],
      lastScan: '5 minutes ago',
      icon: MessageSquare,
      color: 'text-blue-400'
    },
    {
      id: 3,
      name: 'Salesforce',
      type: 'CRM',
      status: 'active',
      risksDetected: 15,
      channels: 28,
      keywords: ['complaint', 'escalation', 'dissatisfied', 'lawsuit'],
      lastScan: '10 minutes ago',
      icon: Brain,
      color: 'text-cyan-400'
    },
    {
      id: 4,
      name: 'Zendesk',
      type: 'Support',
      status: 'active',
      risksDetected: 23,
      channels: 56,
      keywords: ['bug', 'broken', 'not working', 'frustrated'],
      lastScan: '1 minute ago',
      icon: MessageSquare,
      color: 'text-green-400'
    },
    {
      id: 5,
      name: 'GitHub',
      type: 'Development',
      status: 'warning',
      risksDetected: 7,
      channels: 18,
      keywords: ['security', 'vulnerability', 'exploit', 'breach'],
      lastScan: '15 minutes ago',
      icon: Brain,
      color: 'text-orange-400'
    }
  ];

  const totalRisksDetected = aiIntegrations.reduce((sum, integration) => sum + integration.risksDetected, 0);
  const activeIntegrations = aiIntegrations.filter(i => i.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-blue-500/20">
            <Brain className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">AI Monitoring Integrations</h1>
            <p className="text-neutral-400">Continuous risk detection across communication channels</p>
          </div>
        </div>
        <button className="btn-primary">
          Configure Themis AI
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Active Integrations</p>
              <p className="text-2xl font-bold text-white mt-1">{activeIntegrations}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Risks Detected (24h)</p>
              <p className="text-2xl font-bold text-white mt-1">{totalRisksDetected}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Total Channels</p>
              <p className="text-2xl font-bold text-white mt-1">
                {aiIntegrations.reduce((sum, i) => sum + i.channels, 0)}
              </p>
            </div>
            <MessageSquare className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Avg. Detection Time</p>
              <p className="text-2xl font-bold text-white mt-1">2.3min</p>
            </div>
            <TrendingUp className="w-8 h-8 text-cyan-400" />
          </div>
        </div>
      </div>

      {/* AI Monitoring Configuration */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Themis AI Configuration</h2>
        <div className="space-y-4">
          <div>
            <label className="form-label">Risk Detection Sensitivity</label>
            <select className="form-input w-full">
              <option>High - Detect all potential risks</option>
              <option selected>Medium - Balanced detection (Recommended)</option>
              <option>Low - Only critical risks</option>
            </select>
          </div>

          <div>
            <label className="form-label">Scan Frequency</label>
            <select className="form-input w-full">
              <option selected>Real-time - Continuous monitoring</option>
              <option>Every 5 minutes</option>
              <option>Every 15 minutes</option>
              <option>Hourly</option>
            </select>
          </div>

          <div>
            <label className="form-label">Auto-Create Risk Register Entries</label>
            <div className="flex items-center gap-2 mt-2">
              <input type="checkbox" id="auto-create" className="rounded border-neutral-700" defaultChecked />
              <label htmlFor="auto-create" className="text-sm text-neutral-300">
                Automatically create risk entries when threshold is exceeded
              </label>
            </div>
          </div>

          <div>
            <label className="form-label">Notification Threshold</label>
            <select className="form-input w-full">
              <option>Critical risks only</option>
              <option selected>High and Critical risks</option>
              <option>Medium, High, and Critical risks</option>
              <option>All detected risks</option>
            </select>
          </div>
        </div>
      </div>

      {/* Active AI Integrations */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Active Integrations</h2>
          <button className="btn-outline text-sm">Add Integration</button>
        </div>

        <div className="space-y-4">
          {aiIntegrations.map((integration) => {
            const Icon = integration.icon;
            return (
              <div
                key={integration.id}
                className="bg-neutral-900 rounded-lg p-4 border border-neutral-800 hover:border-neutral-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-neutral-800 rounded-lg">
                      <Icon className={cn("w-6 h-6", integration.color)} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-semibold">{integration.name}</h3>
                        <span className={cn(
                          "badge",
                          integration.status === 'active' ? 'badge-success' : 'badge-warning'
                        )}>
                          {integration.status}
                        </span>
                        <span className="badge bg-neutral-800 text-neutral-300 border border-neutral-700">
                          {integration.type}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-neutral-500">Risks Detected (24h)</p>
                          <p className="text-lg font-semibold text-yellow-400">{integration.risksDetected}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Monitored Channels</p>
                          <p className="text-lg font-semibold text-white">{integration.channels}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Last Scan</p>
                          <p className="text-sm text-neutral-300 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {integration.lastScan}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 mb-2">Monitored Keywords:</p>
                        <div className="flex flex-wrap gap-2">
                          {integration.keywords.map((keyword, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-neutral-800 text-xs text-neutral-300 rounded border border-neutral-700"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-outline text-xs px-3 py-1">Configure</button>
                    <button className="btn-outline text-xs px-3 py-1 text-neutral-400 border-neutral-700">
                      Pause
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Detections */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Risk Detections</h2>
        <div className="space-y-3">
          {[
            { source: 'Slack #engineering', risk: 'Production database connection failures', severity: 'high', time: '2 min ago' },
            { source: 'Teams General', risk: 'Customer complaint about data breach', severity: 'critical', time: '5 min ago' },
            { source: 'Zendesk Ticket #4521', risk: 'Multiple users reporting login issues', severity: 'medium', time: '8 min ago' },
            { source: 'Salesforce Account', risk: 'Enterprise client threatening contract cancellation', severity: 'high', time: '12 min ago' },
            { source: 'GitHub Issue #342', risk: 'Security vulnerability in authentication module', severity: 'critical', time: '15 min ago' }
          ].map((detection, idx) => (
            <div key={idx} className="bg-neutral-900 rounded-lg p-3 border border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <AlertTriangle className={cn(
                  "w-5 h-5",
                  detection.severity === 'critical' ? 'text-red-400' :
                  detection.severity === 'high' ? 'text-orange-400' : 'text-yellow-400'
                )} />
                <div className="flex-1">
                  <p className="text-white font-medium">{detection.risk}</p>
                  <p className="text-sm text-neutral-400">Source: {detection.source}</p>
                </div>
                <span className={cn(
                  "badge text-xs",
                  detection.severity === 'critical' ? 'badge-danger' :
                  detection.severity === 'high' ? 'badge-warning' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                )}>
                  {detection.severity}
                </span>
                <span className="text-sm text-neutral-500">{detection.time}</span>
              </div>
              <button className="btn-outline text-xs px-3 py-1 ml-3">View Details</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
